import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import useScrollSelectedOnOpen from '../../../hooks/useScrollSelectedOnOpen';

const MENU_MAX_HEIGHT = 220;
const MENU_GAP = 8;

/**
 * Dropdown that portals its menu to document.body so it is not clipped by
 * overflow:auto modal containers.
 */
const ModalPortalSelect = ({
  label,
  value,
  placeholder = 'Select...',
  options = [],
  onSelect,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  useScrollSelectedOnOpen(open && !!menuStyle, menuRef);

  const selected = options.find((option) => option.value === value);

  const updatePosition = () => {
    const button = buttonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP;
    const spaceAbove = rect.top - MENU_GAP;
    const openUp = spaceBelow < Math.min(MENU_MAX_HEIGHT, 160) && spaceAbove > spaceBelow;
    const maxHeight = Math.max(120, Math.min(MENU_MAX_HEIGHT, openUp ? spaceAbove : spaceBelow));

    setMenuStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      zIndex: 100,
      maxHeight,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + MENU_GAP }
        : { top: rect.bottom + MENU_GAP }),
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      // Drop stale geometry so the next open waits for a fresh position.
      setMenuStyle(null);
      return undefined;
    }
    updatePosition();
    const onReposition = () => updatePosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const inButton = rootRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inButton && !inMenu) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className={`flex w-full flex-col gap-2 ${className}`} ref={rootRef}>
      {label ? (
        <label className="font-['Inter'] text-[14px] text-[#282A29]">{label}</label>
      ) : null}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-[42px] w-full items-center justify-between rounded-[12px] border border-[#D5D8D6] bg-white px-4 font-['Inter'] text-[16px] text-[#282A29]"
      >
        <span className={`truncate ${selected ? '' : 'text-[#828382]'}`}>
          {selected?.label || placeholder}
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-[#A0A3A1] transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open &&
        menuStyle &&
        createPortal(
          <div
            ref={menuRef}
            style={menuStyle}
            className="overflow-auto rounded-[12px] border border-[#D5D8D6] bg-white py-1 shadow-lg"
          >
            {options.length === 0 ? (
              <div className="px-4 py-2.5 font-['Inter'] text-[15px] text-[#828382]">No options</div>
            ) : (
              options.map((option) => (
                <button
                  key={option.value}
                  data-selected={option.value === value}
                  type="button"
                  onClick={() => {
                    onSelect(option.value);
                    setOpen(false);
                  }}
                  className="block w-full px-4 py-2.5 text-left font-['Inter'] text-[15px] text-[#282A29] hover:bg-[#F3FFF7]"
                >
                  {option.label}
                </button>
              ))
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default ModalPortalSelect;
